import React from 'react';
import { Edit2, Trash2, Eye, MoreVertical, Search, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * A beautiful, reusable DataList component with modern UI/UX
 *
 * @param {Object} props
 * @param {Array} props.data - Array of data items to display
 * @param {Array} props.columns - Column configuration
 * @param {Function} props.onDelete - Callback for delete action
 * @param {Function} props.onEdit - Callback for edit action
 * @param {Function} props.onView - Callback for view action
 * @param {String} props.title - List title
 * @param {String} props.addButtonText - Text for add button
 * @param {String} props.addButtonLink - Link for add button
 * @param {Boolean} props.loading - Loading state
 * @param {String} props.emptyMessage - Message when no data
 * @param {Boolean} props.showSearch - Show search bar
 * @param {Function} props.onSearch - Search callback
 * @param {Boolean} props.cardView - Use card view instead of table
 */
const DataList = ({
  data = [],
  columns = [],
  onDelete,
  onEdit,
  onView,
  title,
  addButtonText,
  addButtonLink,
  loading = false,
  emptyMessage = 'কোন ডেটা পাওয়া যায়নি',
  showSearch = false,
  onSearch,
  cardView = false,
  renderCard,
}) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = React.useState('');

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (onSearch) {
      onSearch(value);
    }
  };

  const handleAddClick = () => {
    if (addButtonLink) {
      navigate(addButtonLink);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="text-gray-500">লোড হচ্ছে...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Header Section */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            {title && (
              <h2 className="text-2xl font-bold text-gray-800">{title}</h2>
            )}
            <p className="text-sm text-gray-500 mt-1">
              মোট {data.length} টি আইটেম
            </p>
          </div>
          {addButtonText && (
            <button
              onClick={handleAddClick}
              className="flex items-center gap-2 bg-primary hover:bg-primary-focus text-white px-4 py-2 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
            >
              <Plus size={20} />
              {addButtonText}
            </button>
          )}
        </div>

        {/* Search Bar */}
        {showSearch && (
          <div className="mt-4 relative">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={20}
            />
            <input
              type="text"
              placeholder="অনুসন্ধান করুন..."
              value={searchTerm}
              onChange={handleSearch}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
            />
          </div>
        )}
      </div>

      {/* Empty State */}
      {data.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <div className="text-gray-400 mb-4">
            <Search size={48} />
          </div>
          <p className="text-lg text-gray-600 font-medium">{emptyMessage}</p>
          {addButtonText && (
            <button
              onClick={handleAddClick}
              className="mt-4 flex items-center gap-2 text-primary hover:text-primary-focus font-medium"
            >
              <Plus size={18} />
              {addButtonText}
            </button>
          )}
        </div>
      ) : cardView ? (
        /* Card View */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.map((item, index) =>
            renderCard ? (
              renderCard(item, index)
            ) : (
              <div
                key={item._id || index}
                className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-200 p-4"
              >
                {/* Default card rendering */}
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    {columns.slice(0, 3).map((col) => (
                      <div key={col.key} className="mb-2">
                        <p className="text-xs text-gray-500">{col.label}</p>
                        <p className="text-sm font-medium">
                          {col.render
                            ? col.render(item[col.key], item)
                            : item[col.key]}
                        </p>
                      </div>
                    ))}
                  </div>
                  <ActionButtons
                    item={item}
                    onView={onView}
                    onEdit={onEdit}
                    onDelete={onDelete}
                  />
                </div>
              </div>
            )
          )}
        </div>
      ) : (
        /* Table View */
        <div className="overflow-x-auto bg-white rounded-lg shadow-md">
          <table className="table w-full">
            <thead>
              <tr className="bg-gray-50 border-b-2 border-gray-200">
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className="text-left px-6 py-4 text-sm font-semibold text-gray-700 uppercase tracking-wider"
                  >
                    {col.label}
                  </th>
                ))}
                {(onView || onEdit || onDelete) && (
                  <th className="text-center px-6 py-4 text-sm font-semibold text-gray-700 uppercase tracking-wider">
                    কার্যক্রম
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data.map((item, index) => (
                <tr
                  key={item._id || index}
                  className="hover:bg-gray-50 transition-colors"
                >
                  {columns.map((col) => (
                    <td key={col.key} className="px-6 py-4 text-sm text-gray-700">
                      {col.render
                        ? col.render(item[col.key], item)
                        : item[col.key]}
                    </td>
                  ))}
                  {(onView || onEdit || onDelete) && (
                    <td className="px-6 py-4">
                      <ActionButtons
                        item={item}
                        onView={onView}
                        onEdit={onEdit}
                        onDelete={onDelete}
                      />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Action Buttons Component
const ActionButtons = ({ item, onView, onEdit, onDelete }) => {
  const handleDelete = () => {
    if (window.confirm('আপনি কি নিশ্চিত যে আপনি এটি মুছে ফেলতে চান?')) {
      onDelete(item._id || item.id);
    }
  };

  return (
    <div className="flex items-center justify-center gap-2">
      {onView && (
        <button
          onClick={() => onView(item._id || item.id)}
          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200 tooltip"
          data-tip="দেখুন"
          title="দেখুন"
        >
          <Eye size={18} />
        </button>
      )}
      {onEdit && (
        <button
          onClick={() => onEdit(item._id || item.id)}
          className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-all duration-200 tooltip"
          data-tip="সম্পাদনা"
          title="সম্পাদনা"
        >
          <Edit2 size={18} />
        </button>
      )}
      {onDelete && (
        <button
          onClick={handleDelete}
          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200 tooltip"
          data-tip="মুছে ফেলুন"
          title="মুছে ফেলুন"
        >
          <Trash2 size={18} />
        </button>
      )}
    </div>
  );
};

export default DataList;
